import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Lock, LogOut, Mail, Phone, Calendar, RefreshCw, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "monplaisir:admin-token";

function authHeaders() {
    const t = localStorage.getItem(TOKEN_KEY);
    return t ? { Authorization: `Bearer ${t}` } : {};
}

function LoginForm({ onSuccess }) {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const { data } = await axios.post(`${API}/admin/login`, { email, password });
            localStorage.setItem(TOKEN_KEY, data.token);
            toast.success("Welcome.");
            onSuccess();
        } catch (err) {
            const d = err?.response?.data?.detail;
            toast.error(typeof d === "string" ? d : "Login failed.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen bg-cream flex items-center justify-center px-6 py-16">
            <form
                onSubmit={submit}
                data-testid="admin-login-form"
                className="w-full max-w-md border border-bone bg-cream/95 px-8 md:px-12 py-12"
            >
                <Link
                    to="/"
                    className="font-sans text-[10px] uppercase tracking-[0.32em] text-oxblood hover:opacity-70"
                >
                    ← Monplaisir
                </Link>
                <h1 className="mt-8 font-serif text-[2.2rem] md:text-[2.6rem] leading-[1.05]">
                    Private <span className="italic">access.</span>
                </h1>
                <p className="mt-3 font-sans text-[13px] text-[#595959] leading-[1.7]">
                    Enter to view enquiries received through the website.
                </p>

                <div className="mt-10 space-y-2">
                    <label className="block">
                        <span className="block font-sans text-[10px] uppercase tracking-[0.32em] text-[#595959] mb-1">
                            Email
                        </span>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="line-input"
                            data-testid="admin-email"
                            required
                            autoComplete="username"
                        />
                    </label>
                    <label className="block">
                        <span className="block font-sans text-[10px] uppercase tracking-[0.32em] text-[#595959] mb-1">
                            Password
                        </span>
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="line-input"
                            data-testid="admin-password"
                            required
                            autoComplete="current-password"
                        />
                    </label>
                </div>

                <button
                    type="submit"
                    disabled={busy}
                    data-testid="admin-login-submit"
                    className="mt-10 w-full inline-flex items-center justify-center bg-oxblood text-[#F9F8F6] font-sans text-[11px] uppercase tracking-[0.32em] py-4 hover:bg-[#4A1410] transition-colors duration-500 disabled:opacity-60"
                >
                    {busy ? "Entering…" : (<><Lock size={14} className="mr-3" /> Enter</>)}
                </button>
            </form>
        </div>
    );
}

function Dashboard({ onLogout }) {
    const [items, setItems] = useState(null);
    const [error, setError] = useState(null);
    const [refreshing, setRefreshing] = useState(false);
    const [expanded, setExpanded] = useState(null);

    const load = useCallback(async () => {
        setRefreshing(true);
        try {
            const { data } = await axios.get(`${API}/admin/enquiries`, {
                headers: authHeaders(),
            });
            setItems(data);
            setError(null);
        } catch (err) {
            if (err?.response?.status === 401) {
                onLogout();
                return;
            }
            setError("Could not load enquiries. Please try again.");
        } finally {
            setRefreshing(false);
        }
    }, [onLogout]);

    useEffect(() => {
        load();
    }, [load]);

    const remove = async (id) => {
        if (!window.confirm("Delete this enquiry permanently?")) return;
        try {
            await axios.delete(`${API}/admin/enquiries/${id}`, { headers: authHeaders() });
            setItems((prev) => prev.filter((e) => e.id !== id));
            toast.success("Enquiry deleted.");
        } catch (err) {
            toast.error("Could not delete.");
        }
    };

    const fmt = (iso) => {
        try {
            const d = new Date(iso);
            return d.toLocaleString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
            });
        } catch {
            return iso;
        }
    };

    return (
        <div className="min-h-screen bg-cream text-[#1A1A1A]">
            {/* Top bar */}
            <header className="border-b border-bone bg-cream/95 backdrop-blur-xl sticky top-0 z-30">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
                    <Link to="/" className="font-serif italic text-[20px] md:text-[22px]">
                        Monplaisir <span className="font-sans not-italic text-[10px] tracking-[0.32em] uppercase text-[#595959] ml-2">Enquiries</span>
                    </Link>
                    <div className="flex items-center gap-4">
                        <button
                            onClick={load}
                            data-testid="admin-refresh"
                            className="flex items-center gap-2 font-sans text-[11px] uppercase tracking-[0.28em] text-[#1A1A1A] hover:text-oxblood transition-colors"
                        >
                            <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} />
                            <span className="hidden md:inline">Refresh</span>
                        </button>
                        <button
                            onClick={onLogout}
                            data-testid="admin-logout"
                            className="flex items-center gap-2 font-sans text-[11px] uppercase tracking-[0.28em] text-[#1A1A1A] hover:text-oxblood transition-colors"
                        >
                            <LogOut size={14} />
                            <span className="hidden md:inline">Sign out</span>
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-[1400px] mx-auto px-6 md:px-12 py-16 md:py-20">
                <div className="mb-12 md:mb-16">
                    <span className="font-sans text-[10px] uppercase tracking-[0.4em] text-oxblood">
                        Confidential
                    </span>
                    <h1 className="mt-4 font-serif text-[2.4rem] md:text-[3.4rem] leading-[1.05]">
                        Received <span className="italic">enquiries.</span>
                    </h1>
                    <p className="mt-3 font-sans text-[14px] text-[#595959]">
                        {items === null
                            ? "Loading…"
                            : `${items.length} record${items.length === 1 ? "" : "s"} on file.`}
                    </p>
                </div>

                {error && (
                    <div className="border border-oxblood/40 bg-[#5E1914]/5 px-6 py-5 mb-10 font-sans text-[13px] text-oxblood">
                        {error}
                    </div>
                )}

                {items !== null && items.length === 0 && !error && (
                    <div data-testid="admin-empty" className="border border-bone px-8 py-16 text-center">
                        <p className="font-serif italic text-[20px] text-oxblood">No enquiries yet.</p>
                        <p className="mt-3 font-sans text-[13px] text-[#595959]">
                            Submissions through the website will appear here.
                        </p>
                    </div>
                )}

                {items !== null && items.length > 0 && (
                    <div className="border-t border-bone" data-testid="admin-enquiry-list">
                        {items.map((e) => {
                            const open = expanded === e.id;
                            return (
                                <div
                                    key={e.id}
                                    data-testid={`admin-enquiry-${e.id}`}
                                    className="border-b border-bone py-6 md:py-8"
                                >
                                    <button
                                        onClick={() => setExpanded(open ? null : e.id)}
                                        className="w-full grid grid-cols-12 gap-4 items-start text-left group"
                                    >
                                        <div className="col-span-12 md:col-span-3">
                                            <div className="font-sans text-[10px] uppercase tracking-[0.28em] text-[#595959]">
                                                {fmt(e.created_at)}
                                            </div>
                                            <div className="mt-1 font-serif text-[18px] md:text-[20px] leading-tight group-hover:text-oxblood transition-colors">
                                                {e.name}
                                            </div>
                                        </div>
                                        <div className="col-span-12 md:col-span-3 font-sans text-[13px] text-[#1A1A1A]/80">
                                            <div className="flex items-center gap-2">
                                                <Mail size={12} className="text-[#C5A880]" /> {e.email}
                                            </div>
                                            {e.phone && (
                                                <div className="mt-1 flex items-center gap-2">
                                                    <Phone size={12} className="text-[#C5A880]" /> {e.phone}
                                                </div>
                                            )}
                                        </div>
                                        <div className="col-span-12 md:col-span-3 font-sans text-[13px]">
                                            <div className="text-[#1A1A1A]/80">{e.service}</div>
                                            {e.date && (
                                                <div className="mt-1 flex items-center gap-2 text-[#595959]">
                                                    <Calendar size={12} className="text-[#C5A880]" /> {e.date}
                                                </div>
                                            )}
                                        </div>
                                        <div className="col-span-12 md:col-span-3 font-sans text-[13px] text-[#1A1A1A]/70 leading-snug md:text-right">
                                            <span className="md:hidden font-sans text-[10px] uppercase tracking-[0.28em] text-[#595959] block mb-1">
                                                Request
                                            </span>
                                            {open
                                                ? "Click to collapse"
                                                : `${e.message.slice(0, 90)}${e.message.length > 90 ? "…" : ""}`}
                                        </div>
                                    </button>

                                    {open && (
                                        <div className="mt-6 grid grid-cols-12 gap-4">
                                            <div className="col-span-12 md:col-span-9">
                                                <div className="font-sans text-[10px] uppercase tracking-[0.32em] text-oxblood mb-2">
                                                    The Request
                                                </div>
                                                <p className="font-sans text-[14px] leading-[1.85] whitespace-pre-wrap text-[#1A1A1A]/85">
                                                    {e.message}
                                                </p>
                                                <div className="mt-6 flex flex-wrap gap-4">
                                                    <a
                                                        href={`mailto:${e.email}?subject=Re%3A%20Monplaisir%20enquiry`}
                                                        className="font-sans text-[11px] uppercase tracking-[0.3em] text-oxblood border-b border-oxblood pb-1 hover:opacity-70"
                                                    >
                                                        Reply by email
                                                    </a>
                                                    {e.phone && (
                                                        <a
                                                            href={`tel:${e.phone.replace(/\s/g, "")}`}
                                                            className="font-sans text-[11px] uppercase tracking-[0.3em] text-oxblood border-b border-oxblood pb-1 hover:opacity-70"
                                                        >
                                                            Call
                                                        </a>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="col-span-12 md:col-span-3 md:text-right">
                                                <button
                                                    onClick={() => remove(e.id)}
                                                    data-testid={`admin-delete-${e.id}`}
                                                    className="inline-flex items-center gap-2 font-sans text-[10px] uppercase tracking-[0.3em] text-[#595959] hover:text-oxblood transition-colors"
                                                >
                                                    <Trash2 size={12} /> Delete
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </main>
        </div>
    );
}

export default function Admin() {
    const [authed, setAuthed] = useState(!!localStorage.getItem(TOKEN_KEY));
    const navigate = useNavigate();

    const logout = useCallback(() => {
        localStorage.removeItem(TOKEN_KEY);
        setAuthed(false);
    }, []);

    // If a stale token is in storage, the API will 401 and Dashboard calls logout.
    return authed ? <Dashboard onLogout={logout} /> : <LoginForm onSuccess={() => setAuthed(true)} />;
}
